import { MenuItem, type SelectChangeEvent } from "@mui/material";
import type { Editor } from "@tiptap/core";
import { MenuSelect, useRichTextEditorContext } from "mui-tiptap";
import { useTranslation } from "react-i18next";
import { applyTextFormat, type TextFormat } from "./textFormat";

const headingLevels = {
  heading2: 2,
  heading3: 3,
  heading4: 4,
} as const;

function getCurrentTextFormat(editor: Editor | null): TextFormat | "" {
  if (editor?.isActive("paragraph")) {
    return "paragraph";
  }

  for (const [format, level] of Object.entries(headingLevels)) {
    if (editor?.isActive("heading", { level })) {
      return format as Exclude<TextFormat, "paragraph">;
    }
  }

  return "";
}

export default function MenuSelectTextFormat() {
  const editor = useRichTextEditorContext();
  const { t } = useTranslation();
  const currentFormat = getCurrentTextFormat(editor);
  const canSetParagraph =
    currentFormat === "paragraph" || Boolean(editor?.can().setParagraph());
  const canSetHeading =
    currentFormat.startsWith("heading") ||
    Boolean(editor?.can().setHeading({ level: 2 }));
  const isFormatChangeAvailable =
    currentFormat !== "" || canSetParagraph || canSetHeading;

  const handleChange = (event: SelectChangeEvent<TextFormat>) => {
    if (!editor) return;

    applyTextFormat(editor, event.target.value as TextFormat);
  };

  return (
    <MenuSelect
      disabled={!editor?.isEditable || !isFormatChangeAvailable}
      displayEmpty
      inputProps={{ "aria-label": t("textFormat") }}
      MenuProps={{
        sx: {
          zIndex: (theme) => theme.zIndex.tooltip + 1,
        },
      }}
      onChange={handleChange}
      sx={{
        "&&& .MuiOutlinedInput-notchedOutline": {
          border: 0,
        },
      }}
      tooltipTitle={t("changeTextFormat")}
      value={currentFormat}
    >
      <MenuItem disabled={!canSetParagraph} value="paragraph">
        {t("paragraph")}
      </MenuItem>
      <MenuItem disabled={!canSetHeading} value="heading2">
        {t("heading2")}
      </MenuItem>
      <MenuItem disabled={!canSetHeading} value="heading3">
        {t("heading3")}
      </MenuItem>
      <MenuItem disabled={!canSetHeading} value="heading4">
        {t("heading4")}
      </MenuItem>
    </MenuSelect>
  );
}